import { useState } from 'react'
import { useNavigate } from 'react-router'
import { createCheckout, updateSeats } from '@/lib/billing-api'

interface UpgradePromptProps {
  currentChildren: number
  currentLimit: number
  hasSubscription?: boolean
  onClose?: () => void
}

export function UpgradePrompt({ currentChildren, currentLimit, hasSubscription = false, onClose }: UpgradePromptProps) {
  const navigate = useNavigate()
  const [seats, setSeats] = useState(Math.max(2, currentChildren + 1))
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const monthlyTotal = seats * 2

  const handleUpgrade = async () => {
    setError(null)
    setIsLoading(true)

    try {
      if (hasSubscription) {
        // Update existing subscription via Salable API
        await updateSeats(seats)
        setSuccess(true)
        setTimeout(() => {
          navigate('/settings', { replace: true })
          window.location.reload()
        }, 1500)
      } else {
        // Create new subscription via checkout
        const { checkoutUrl } = await createCheckout(seats)
        window.location.href = checkoutUrl
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subscription')
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-lg">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Subscription Updated!</h2>
          <p className="mt-1 text-sm text-gray-500">
            You now have {seats} child profiles. Redirecting...
          </p>
        </div>
      </div>
    )
  }

  // For existing subscribers, show seat selector to add more seats
  if (hasSubscription) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Add More Profiles</h2>
          <p className="mt-1 text-sm text-gray-500">
            You've reached your limit of {currentLimit} child profile{currentLimit !== 1 ? 's' : ''}
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <label htmlFor="seats-existing" className="mb-2 block text-sm font-medium text-gray-700">
            How many child profiles do you need?
          </label>
          <select
            id="seats-existing"
            value={seats}
            onChange={(e) => setSeats(Number(e.target.value))}
            className="w-full rounded border border-gray-300 px-3 py-2"
            disabled={isLoading}
          >
            {Array.from({ length: 99 }, (_, i) => i + 2).map((num) => {
              const isCurrent = num === currentLimit
              return (
                <option key={num} value={num} disabled={isCurrent}>
                  {num} child profiles - £{num * 2}/month
                  {isCurrent ? ' (current)' : ''}
                </option>
              )
            })}
          </select>

          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-sm text-gray-500">New monthly cost</span>
            <span className="text-2xl font-bold text-gray-900">
              £{monthlyTotal}<span className="text-sm font-normal text-gray-500">/month</span>
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            £2 per child profile per month
          </p>
          {seats < currentChildren && (
            <div className="mt-3 rounded bg-amber-100 p-3 text-sm text-amber-800">
              <strong>Warning:</strong> You have {currentChildren} profiles but are selecting {seats} seats.
              The {currentChildren - seats} newest profile{currentChildren - seats > 1 ? 's' : ''} will be locked (read-only).
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleUpgrade}
            disabled={isLoading || seats === currentLimit}
            className="btn-primary flex-1"
          >
            {isLoading ? 'Updating...' : seats < currentLimit ? 'Downgrade' : 'Upgrade'}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          Your subscription will be prorated
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg">
      <div className="mb-4 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Upgrade Required</h2>
        <p className="mt-1 text-sm text-gray-500">
          You've reached your free limit of 1 child profile
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 rounded-lg bg-gray-50 p-4">
        <label htmlFor="seats" className="mb-2 block text-sm font-medium text-gray-700">
          How many child profiles do you need?
        </label>
        <select
          id="seats"
          value={seats}
          onChange={(e) => setSeats(Number(e.target.value))}
          className="w-full rounded border border-gray-300 px-3 py-2"
          disabled={isLoading}
        >
          {Array.from({ length: 99 }, (_, i) => i + 2).map((num) => (
            <option key={num} value={num}>
              {num} child profiles
            </option>
          ))}
        </select>

        <div className="mt-4 flex items-baseline justify-between">
          <span className="text-sm text-gray-500">Monthly cost</span>
          <span className="text-2xl font-bold text-gray-900">
            £{monthlyTotal}<span className="text-sm font-normal text-gray-500">/month</span>
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          £2 per child profile per month
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleUpgrade}
          disabled={isLoading}
          className="btn-primary flex-1"
        >
          {isLoading ? 'Loading...' : 'Upgrade Now'}
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
      </div>

      <p className="mt-4 text-center text-xs text-gray-400">
        Secure payment via Stripe
      </p>
    </div>
  )
}
